import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Chrome } from 'lucide-react'; // Google icon substitute

export default function Auth() {
  const [isLogin, setIsLogin] = useState(true);
  const [role, setRole] = useState('buyer');
  const [formData, setFormData] = useState({ email: '', password: '', name: '', store_name: '' });
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();

  React.useEffect(() => {
    const roleParam = searchParams.get('role');
    if (roleParam === 'seller') {
      setRole('seller');
      setIsLogin(false);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success("Welcome back!");
      } else {
        await register({ ...formData, role });
        toast.success("Account created!");
      }
      navigate(role === 'seller' ? '/dashboard' : '/marketplace');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Authentication failed");
    }
  };

  const handleGoogleLogin = () => {
    // Dynamic redirect URL
    const redirectUrl = window.location.origin; // e.g., https://...preview.emergentagent.com
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-stone-50">
      <div className="max-w-md w-full space-y-8 glass-panel p-8 rounded-2xl">
        <div>
          <h2 className="text-center text-3xl font-extrabold text-stone-900 font-heading">
            {isLogin ? t('login') : t('register')}
          </h2>
          <p className="mt-2 text-center text-sm text-stone-600">
            {isLogin ? "Welcome back to MadeinDZ" : "Join our community of artisans and buyers"}
          </p>
        </div>

        <Button 
          type="button" 
          variant="outline" 
          className="w-full flex items-center justify-center gap-2 py-6 text-stone-700 font-medium border-stone-300 hover:bg-stone-50"
          onClick={handleGoogleLogin}
        >
          <Chrome className="w-5 h-5" /> {/* Simplistic representation */}
          Continue with Google
        </Button>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-stone-200" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-white px-2 text-stone-500">Or continue with email</span>
          </div>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {!isLogin && (
            <div className="flex justify-center mb-6">
              <RadioGroup value={role} onValueChange={setRole} className="flex gap-4">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="buyer" id="buyer" />
                  <Label htmlFor="buyer">I want to Buy</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="seller" id="seller" />
                  <Label htmlFor="seller">I want to Sell</Label>
                </div>
              </RadioGroup>
            </div>
          )}

          <div className="space-y-4">
            {!isLogin && (
              <div>
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  required
                  className="mt-1"
                  value={formData.name}
                  onChange={e => setFormData({...formData, name: e.target.value})}
                />
              </div>
            )}
            
            {!isLogin && role === 'seller' && (
               <div>
                <Label htmlFor="store_name">Store Name</Label>
                <Input
                  id="store_name"
                  required
                  className="mt-1"
                  value={formData.store_name}
                  onChange={e => setFormData({...formData, store_name: e.target.value})}
                />
              </div>
            )}

            <div>
              <Label htmlFor="email">Email address</Label>
              <Input
                id="email"
                type="email"
                required
                className="mt-1"
                value={formData.email}
                onChange={e => setFormData({...formData, email: e.target.value})}
              />
            </div>

            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                required
                className="mt-1"
                value={formData.password}
                onChange={e => setFormData({...formData, password: e.target.value})}
              />
            </div>
          </div>

          <Button type="submit" className="w-full btn-primary">
            {isLogin ? t('login') : t('register')}
          </Button>

          <div className="text-center">
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-sm text-primary hover:underline font-medium"
            >
              {isLogin ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
